/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

/* eslint-disable @typescript-eslint/no-empty-interface */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, createContext, useContext, ReactNode } from 'react';
import {
  PushNotifications,
  Token,
  PushNotificationSchema,
  ActionPerformed,
} from '@capacitor/push-notifications';
import * as Sentry from '@sentry/capacitor';

import { AACPushNotification } from '@atomic.io/action-cards-web-sdk';
import { Capacitor } from '@capacitor/core';
import { useNavigate } from 'react-router-dom';

// Define the context type
interface PushNotificationsContextType {
  token: Token | undefined;
  onNotificationTapped: (callback: (notification: ActionPerformed) => void) => void;
  onNotificationReceived: (callback: (notification: PushNotificationSchema) => void) => void;
}

// Create a context with a default value
const PushNotificationsContext = createContext<PushNotificationsContextType | undefined>(undefined);

// Custom hook to use the push notifications context
export const usePushNotifications = (): PushNotificationsContextType => {
  const context = useContext(PushNotificationsContext);
  if (context === undefined) {
    throw new Error('usePushNotifications must be used within a PushNotificationsProvider');
  }
  return context;
};

// Custom hook to use the push notifications context
export const usePushNotificationRegistrationToken = (): Token | undefined => {
  const context = useContext(PushNotificationsContext);
  if (context === undefined) {
    throw new Error('usePushNotifications must be used within a PushNotificationsProvider');
  }
  return context.token;
};

// Define props for the provider
interface PushNotificationsProviderProps {
  children: ReactNode;
}

export const requestPush = () => {
  // Request permission to use push notifications
  // iOS will prompt user and return if they granted permission or not
  // Android will just grant without prompting on older version, same as ios on newer.
  PushNotifications.requestPermissions().then((result) => {
    if (result.receive === 'granted') {
      // Register with Apple / Google to receive push via APNS/FCM
      PushNotifications.register();
      return true;
    } else {
      // Show some error
      console.error('Push notifications permission not granted', result.receive);
      if (Capacitor.DEBUG) {
        console.log(
          'Push Notification permissions were denied, and we are unable to ask again. To enable push notifications, please go to FarmlandsPRO app in App Settings and allow notifications.',
        );
      }

      return false;
    }
  });
};
const PushNotificationsProvider: React.FC<PushNotificationsProviderProps> = ({ children }) => {
  const [currentToken, setCurrentToken] = React.useState<Token>();
  const notificationTappedCallbacks = React.useRef<((notification: ActionPerformed) => void)[]>([]);
  const notificationReceivedCallbacks = React.useRef<
    ((notification: PushNotificationSchema) => void)[]
  >([]);
  const handleNotificationTapped = (notification: ActionPerformed) => {
    notificationTappedCallbacks.current.forEach((callback) => callback(notification));
  };

  const navigate = useNavigate();

  const handleNotificationReceived = (notification: PushNotificationSchema) => {
    notificationReceivedCallbacks.current.forEach((callback) => callback(notification));
  };

  const onNotificationTapped = (callback: (notification: ActionPerformed) => void) => {
    notificationTappedCallbacks.current.push(callback);
    return () => {
      notificationTappedCallbacks.current = notificationTappedCallbacks.current.filter(
        (cb) => cb !== callback,
      );
    };
  };

  const onNotificationReceived = (callback: (notification: PushNotificationSchema) => void) => {
    notificationReceivedCallbacks.current.push(callback);
    return () => {
      notificationReceivedCallbacks.current = notificationReceivedCallbacks.current.filter(
        (cb) => cb !== callback,
      );
    };
  };

  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      PushNotifications.register();

      // On success, we should be able to receive notifications
      PushNotifications.addListener('registration', (token: Token) => {
        console.info('Push registration success, token' + token.value);
        setCurrentToken(token);
      });

      // Some issue with our setup and push will not work
      PushNotifications.addListener('registrationError', (error: any) => {
        Sentry.captureMessage('Push registration error: ' + error);
        console.error(error);
      });

      // Show us the notification payload if the app is open on our device
      PushNotifications.addListener(
        'pushNotificationReceived',
        (notification: PushNotificationSchema) => {
          if (Capacitor.DEBUG) {
            console.log('Push received: ' + JSON.stringify(notification));
          }
          console.log('Push action performed: ' + JSON.stringify(notification));
          // }

          localStorage.setItem('last_apns', JSON.stringify(notification));
          handleNotificationReceived(notification);
        },
      );

      // Method called when tapping on a notification
      PushNotifications.addListener(
        'pushNotificationActionPerformed',
        (notification: ActionPerformed) => {
          console.log(notification);
          console.log('Push action performed: ' + JSON.stringify(notification));

          localStorage.setItem('last_apns', JSON.stringify(notification));

          if (Object.keys(notification.notification.data).includes('atomic')) {
            console.log(notification);
            const data = JSON.parse(notification.notification.data?.atomic) as AACPushNotification;
            const detail = data.detail as {
              url: string;
              ext_url: string;
              streamContainerId: string;
            };
            if (Object.keys(detail).includes('url')) {
              setTimeout(() => {
                navigate(detail['url']);
              }, 500);
            } else if (Object.keys(detail).includes('ext_url')) {
              setTimeout(() => {
                window.open(detail['ext_url'], '_blank');
              }, 500);
            }
          }
          handleNotificationTapped(notification);
        },
      );
    }
  }, []);

  const value = React.useMemo(() => {
    return {
      token: currentToken,
      onNotificationTapped,
      onNotificationReceived,
    };
  }, [currentToken]);

  return (
    <PushNotificationsContext.Provider value={value}>{children}</PushNotificationsContext.Provider>
  );
};

export default PushNotificationsProvider;
